<!--
Copyright (C) 2025 Checkmk GmbH - License: GNU General Public License v2
This file is part of Checkmk (https://checkmk.com). It is subject to the terms and
conditions defined in the file COPYING, which is part of this source code package.
-->
<script setup lang="ts">
import { type Ref, computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'

import { useDebounceFn } from '@/lib/useDebounce'
import useTimer from '@/lib/useTimer.ts'

import CmkIcon from '@/components/CmkIcon'

import { useInjectCmkToken } from '@/dashboard/composables/useCmkToken'
import { useSuppressEventOnPublicDashboard } from '@/dashboard/composables/useIsPublicDashboard'
import type { FilterHTTPVars, GraphWidgetContent } from '@/dashboard/types/widget.ts'

import DashboardContentContainer from './DashboardContentContainer.vue'
import type { ContentProps } from './types.ts'

const props = defineProps<ContentProps>()
const cmkToken = useInjectCmkToken()
const suppressEventOnPublicDashboard = useSuppressEventOnPublicDashboard()
const dataEndpointUrl: Ref<string> = computed(() => {
  return cmkToken ? 'widget_graph_token_auth.py' : 'widget_graph.py'
})

// @ts-expect-error comes from different javascript file
const cmkToolkit = window['cmk']

const contentDiv = ref<HTMLDivElement | null>(null)
const parentDiv = computed(() => contentDiv.value?.parentElement || null)
const isLoading = ref<boolean>(true)

const resolveObservedElement = (): HTMLElement | null => {
  if (props.isPreview && showLegend.value && contentDiv.value) {
    return (
      (contentDiv.value.closest(
        '.db-content-container__preview-scroll-container'
      ) as HTMLElement) ?? parentDiv.value
    )
  }
  return parentDiv.value
}

const httpVars: Ref<FilterHTTPVars> = computed(() => {
  if (cmkToken !== undefined) {
    return {
      widget_id: props.widget_id,
      'cmk-token': cmkToken
    }
  }
  return {
    widget_id: props.widget_id,
    content: JSON.stringify(props.content),
    context: JSON.stringify(props.effective_filter_context.filters),
    single_infos: JSON.stringify(props.effective_filter_context.uses_infos)
  }
})
const sizeVars: Ref<FilterHTTPVars> = ref({
  width: '0',
  height: '0'
})

const handleRefreshData = (_handlerData: null, body: string) => {
  if (contentDiv.value) {
    contentDiv.value.innerHTML = body
    cmkToolkit.utils.execute_javascript_by_object(contentDiv.value)
  }
  isLoading.value = false
  timer.reportSuccess()
}

const handleRefreshError = () => {
  timer.reportFailure()
}

const updateGraph = () => {
  cmkToolkit.ajax.call_ajax(dataEndpointUrl.value, {
    post_data: new URLSearchParams({ ...httpVars.value, ...sizeVars.value }).toString(),
    method: 'POST',
    response_handler: handleRefreshData,
    error_handler: handleRefreshError
  })
}
const debouncedUpdateGraph = useDebounceFn(updateGraph, 300)

watch([httpVars, sizeVars], () => {
  debouncedUpdateGraph()
})

const resizeObserver = new ResizeObserver((entries) => {
  // only one element needs to be observed
  const entry = entries[0]!

  const size = entry.contentBoxSize![0]!
  sizeVars.value = {
    width: String(size.inlineSize),
    height: String(size.blockSize)
  }
})

const showLegend = computed(
  () => (props.content as GraphWidgetContent).graph_render_options?.show_legend ?? false
)
const timer = useTimer(updateGraph, 60_000)

let currentObservedElement: HTMLElement | null = null
const reconnectResizeObserver = () => {
  const newElement = resolveObservedElement()
  if (newElement !== currentObservedElement) {
    if (currentObservedElement) {
      resizeObserver.unobserve(currentObservedElement)
    }
    if (newElement) {
      resizeObserver.observe(newElement)
    }
    currentObservedElement = newElement
  }
}

// Reconnect the ResizeObserver after DOM updates when the scroll container appears/disappears
watch([showLegend, () => props.isPreview], () => {
  void nextTick(() => {
    reconnectResizeObserver()
  })
})

onMounted(() => {
  timer.start()
  reconnectResizeObserver()
})

onBeforeUnmount(() => {
  timer.stop()
  resizeObserver.disconnect()
  isLoading.value = false
})
</script>

<template>
  <DashboardContentContainer
    :effective-title="effectiveTitle"
    :general_settings="general_settings"
    content-overflow="hidden"
    :is-scrollable-preview="isPreview && showLegend"
  >
    <CmkIcon
      v-show="isLoading"
      name="load-graph"
      size="xlarge"
      class="db-content-graph__loading-icon"
    />
    <div
      v-show="!isLoading"
      :id="`db-content-graph-${widget_id}`"
      ref="contentDiv"
      class="db-content-graph"
      :class="{ 'db-content-graph__preview': isPreview && !showLegend }"
      @click.capture="suppressEventOnPublicDashboard"
      @auxclick.capture="suppressEventOnPublicDashboard"
      @keydown.capture="suppressEventOnPublicDashboard"
    />
  </DashboardContentContainer>
</template>

<style scoped>
.db-content-graph__loading-icon {
  margin: auto;
}

.db-content-graph__preview {
  pointer-events: none;
}
</style>
